import { ComponentChild, ComponentProps, Ref, RefObject } from 'preact';
import { useState } from 'preact/hooks';
import { forwardRef, createRef, useEffect, useCallback, useRef } from 'preact/compat';

import { useCheckboxRadioContext } from '../PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { IconUserAssistance, InlineUserAssistance } from '../UNSAFE_UserAssistance';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useId } from '../hooks/UNSAFE_useId';
import { StyledCheckbox } from '../PRIVATE_StyledCheckbox';
import { CheckboxRedwoodTheme } from '../UNSAFE_Checkbox/themes/redwood/CheckboxTheme';
import { useActive } from '../hooks/UNSAFE_useActive';
import {
  CheckboxVariantOptions,
  CheckboxStyles
} from '../UNSAFE_Checkbox/themes/CheckboxStyles.css';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { LayoutColumnSpan, layoutSpanStyles } from '#utils/UNSAFE_styles/Layout';
import { classNames } from '#utils/UNSAFE_classNames';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type StyledCheckboxProps = ComponentProps<typeof StyledCheckbox>;

type CheckboxControlProps = Pick<
  StyledCheckboxProps,
  | 'aria-describedby'
  | 'name'
  | 'value'
  | 'onChange'
  | 'onKeyUp'
  | 'isReadonly'
  | 'isDisabled'
  | 'isRequired'
> &
  Pick<InlineUserAssistanceProps, 'assistiveText' | 'helpSourceLink' | 'helpSourceText'> & {
    /**
     * Text that describes the checkbox value.
     */
    children: ComponentChild;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * Whether the input element is checked.
     */
    isChecked?: boolean;
  };

/**
 * CheckboxControl is an internal subcomponent used by both Checkbox and CheckboxItem.
 * It handles styling, placement of label, assistive text, and accessibility text for both parent components.
 * onCommit handling is handled in the parent components.
 */
const CheckboxControl = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      name,
      value,
      children,
      isChecked,
      isDisabled,
      isReadonly,
      isRequired,
      onChange,
      onKeyUp,
      assistiveText,
      helpSourceLink,
      helpSourceText,
      columnSpan = 1
    }: CheckboxControlProps,
    ref: Ref<HTMLInputElement> = null
  ) => {
    // hooks
    const id = useId();
    const childWrapperRef = useRef<HTMLSpanElement>(null);
    const [readonlyLabel, setReadonlyLabel] = useState<string>();
    const { isTabbable } = useTabbableMode();
    const { isActive, activeProps } = useActive();
    const { isFocusRingShown } = useCheckboxRadioContext();
    const labelRef = createRef<HTMLLabelElement>();
    const {
      isFocus,
      focusProps: { onFocus, onBlur }
    } = useFocus();

    // styling
    const {
      classes: checkboxClasses,
      styles: { valueLabelStyle, checkboxSpanStyle }
    } = useComponentTheme<CheckboxVariantOptions, CheckboxStyles>(CheckboxRedwoodTheme, {
      cursor: isDisabled || isReadonly ? 'arrow' : 'pointer',
      disabled: isDisabled ? 'isDisabled' : 'notDisabled'
    });

    // help available
    const hasUserAssistive = !isDisabled && !isReadonly && !!(assistiveText || helpSourceLink);
    const helpAvailableId = hasUserAssistive ? `${id}-help-available` : undefined;
    const uaId = hasUserAssistive ? `${id}-icon-ua` : undefined;
    const { formControl_helpAvailable } = useTranslationBundle<BundleType>(
      '@oracle/oraclejet-preact'
    );
    const helpAvailableString = formControl_helpAvailable();
    const userAssistiveHelp = hasUserAssistive ? (
      <HiddenAccessible id={helpAvailableId}>{helpAvailableString}</HiddenAccessible>
    ) : undefined;

    // prevent auto scrolling
    const onKeyDown = (event: KeyboardEvent) => {
      const { key } = event;
      if (!isReadonly && key !== undefined && key === ' ') {
        event.preventDefault();
      }
    };

    // we want to prevent mouseDown on Links to set active
    const onMouseDownHandler = (e: Event) => {
      // check to see if the target is within an <a> tag and the tag is within the label
      const closestAnchor = (e.target as Element).closest('a');
      if (closestAnchor && childWrapperRef.current?.contains(closestAnchor)) {
        e.stopPropagation();
      } else {
        activeProps['onMouseDown']?.(e);
      }
    };

    // don't allow key down to set active state
    delete activeProps['onKeyDown'];
    const { focusProps, isFocused: isFocusedWithin } = useFocusWithin();

    // JET-70100 - ensure checkbox is scrolled into view when focused and focus ring is shown
    const scrollRef = useRef<HTMLInputElement>(null);
    const inputRef = ref || scrollRef;

    useEffect(() => {
      const isActiveElement =
        document.activeElement === (inputRef as RefObject<HTMLInputElement>)?.current;
      if (!isActiveElement && isFocus && isFocusRingShown) {
        labelRef?.current?.scrollIntoView({ block: 'nearest' });
      }
    }, [inputRef, isFocus, isFocusRingShown, labelRef]);

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    useEffect(() => {
      if (isReadonly) {
        // JET-72374: We can't use the label content on its own for readonly because we also need to provide the state of the checkbox.
        // We also need to update the styled checkbox aria-label once the label ref is set. The textContent is used because the label may contain a link.
        const accessibilityText = `${
          (childWrapperRef.current?.textContent as string) || ''
        } ${translations.formControl_readOnly()} ${
          isChecked ? translations.checkbox_selected() : translations.checkbox_unselected()
        }`;

        setReadonlyLabel(accessibilityText);
      }
    }, [childWrapperRef, isChecked, isReadonly, translations]);

    const onClickHandler = useCallback(() => {
      // when in readonly mode, inputRef is a div
      (inputRef as RefObject<HTMLDivElement>).current?.focus();
    }, [inputRef]);

    const onMouseDown = useCallback((e: MouseEvent) => {
      // JET-76080: prevent default on mouse down events to prevent focus from being
      // dropped to the body when interacting with the clickable label
      e.preventDefault();
    }, []);

    // The top level element needs the column span class on it.
    const classes = layoutSpanStyles.layoutSpanColumn[columnSpan];

    return (
      <label
        ref={labelRef}
        class={classNames([checkboxClasses, classes])}
        onClick={isReadonly ? onClickHandler : undefined}
        onMouseDown={onMouseDown}>
        <span class={checkboxSpanStyle} {...activeProps}>
          <StyledCheckbox
            aria-describedby={ariaDescribedBy}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            isActive={isActive}
            isChecked={isChecked ? 'checked' : 'unchecked'}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            isRequired={isRequired}
            ref={inputRef}
            aria-label={isReadonly ? readonlyLabel : undefined}
          />
        </span>
        <span class={valueLabelStyle}>
          <span
            {...activeProps}
            {...focusProps}
            onMouseDown={onMouseDownHandler}
            ref={childWrapperRef}>
            {children}
          </span>
          {userAssistiveHelp}
          {hasUserAssistive && (
            <IconUserAssistance
              assistiveText={assistiveText}
              helpSourceLink={helpSourceLink}
              helpSourceText={helpSourceText}
              isTabbable={isTabbable && (isFocus || isFocusedWithin)}
              id={uaId}
              variant="checkbox"
            />
          )}
        </span>
      </label>
    );
  }
);

export { CheckboxControl };
